import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import { Button, Icon24ArrowRight, Typography } from "@cur8/maneki";
import styles from "./styles.module.sass";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { paths } from "render/routes/paths";
import { RadioGroup } from "render/ui/presentation/RadioGroup";
import { useMemo, useState } from "react";
import { getAndParseLocationCookie } from "lib/nekoCookies";
import { usePatientQueries } from "hooks/queries/usePatientQuery";
import { useTodaysScheduleQuery } from "hooks/queries/useTodaysScheduleQuery";
import { useVisitQueries } from "hooks/queries/useVisitQuery";
import { APITypesV1 } from "@cur8/api-client";
import { InformationLayout } from "render/ui/layout/InformationLayout";
import { useVisitHistoryQueries } from "hooks/queries/useVisitHistoryQuery";
import { generateVisitTypeLabel } from "lib/generateVisitTypeLabel";

export function SelectVisitView({
  goTo,
  onBack,
}: {
  goTo: (url: string) => void;
  onBack(): void;
}) {
  const location = getAndParseLocationCookie();
  const siteId = location?.siteId;

  const scheduleQuery = useTodaysScheduleQuery({ siteId });
  const visitsData = useVisitQueries(scheduleQuery.data);
  const scheduledVisits = visitsData
    .filter((visit) => visit.data?.status === APITypesV1.VisitStatus.Scheduled)
    .sort((a, b) => {
      if (a.data == null) {
        return 1;
      }
      if (b.data == null) {
        return -1;
      }
      return a.data.startTime < b.data.startTime ? -1 : 1;
    });
  const patientIds = scheduledVisits.map((visit) => visit.data?.patientId);
  const patientData = usePatientQueries(patientIds);
  const visitHistoryData = useVisitHistoryQueries(patientIds);
  const [selectedVisitId, setSelectedVisitId] = useState<string | undefined>(
    undefined
  );

  const selectedVisit = useMemo(() => {
    return scheduledVisits.find(
      (visit) => visit.data?.visitId === selectedVisitId
    );
  }, [selectedVisitId, scheduledVisits]);

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <div className={styles.title}>
              <Typography variant="display-s">Manual check-in</Typography>
              <Typography variant="body-m" color="subtle">
                Select the member you want to check in.
              </Typography>
            </div>
            {scheduledVisits.length === 0 ? (
              <Typography variant="body-m">
                No scheduled visits today
              </Typography>
            ) : undefined}
            <RadioGroup
              aria-label="select visit"
              onChange={(value: string) => setSelectedVisitId(value)}
            >
              {scheduledVisits.map(({ data: visit }, index) => {
                if (visit?.visitId == null) {
                  return undefined;
                }
                const member = patientData.at(index)?.data;
                const visitHistory = visitHistoryData.at(index)?.data;
                const fullName = `${member?.name?.firstName} ${member?.name?.lastName}`;

                const visitTypeLabel = (
                  <Typography variant="body-m" as="span" color="subtle">
                    {visitHistory != null
                      ? generateVisitTypeLabel({ visit, visitHistory })
                      : undefined}
                  </Typography>
                );

                const dob = member?.dateOfBirth?.toISODate();

                const nameAndDob = (
                  <span>
                    <Typography variant="label-m" as="span">
                      {fullName}
                    </Typography>
                    <Typography variant="body-m" color="subtle" as="span">
                      {" "}
                      · {dob}
                    </Typography>
                  </span>
                );

                const time = `${visit?.startTime.toFormat(
                  "HH:mm"
                )} - ${visit?.endTime.toFormat("HH:mm")}`;

                const timeLabel = (
                  <Typography variant="body-m" color="subtle" as="span">
                    {time}
                  </Typography>
                );

                return (
                  <RadioGroup.Radio
                    key={visit.visitId}
                    value={visit?.visitId}
                    label={nameAndDob}
                    bottomLeft={visitTypeLabel}
                    bottomRight={timeLabel}
                  />
                );
              })}
            </RadioGroup>
          </section>
        }
        buttons={
          <ActionsFooter
            left={
              <Button variant="secondary" onPress={onBack} fillContainer>
                Back
              </Button>
            }
            right={
              <Button
                state={!selectedVisit ? "disabled" : undefined}
                onPress={() => {
                  if (!selectedVisit?.data) {
                    return;
                  }
                  goTo(
                    paths.selectVisitConfirm.url({
                      visitId: selectedVisit.data.visitId,
                      patientId: selectedVisit.data.patientId,
                    })
                  );
                }}
                fillContainer
                icon={<Icon24ArrowRight />}
              >
                Continue
              </Button>
            }
          />
        }
      />
    </PageFrameContent>
  );
}
